"use client";

/**
 * AdminContainer — 관리자 통합 컨테이너 (반응형 자동 전환)
 *
 * - 데스크탑: 좌측 사이드바 + 헤더
 * - 모바일: 상단 헤더 + 햄버거 Drawer
 * 뷰포트 너비에 따라 자동으로 전환된다.
 *
 * nav 없이 사용하면 순수 컨테이너로 동작 (로그인 페이지 등).
 * maxWidth 로 모바일 웹뷰 너비를 제한할 수 있다.
 *
 * @example
 * // app/layout.tsx — 로그인 포함 모든 페이지 (모바일 너비 제한)
 * <AdminContainer maxWidth={430}>{children}</AdminContainer>
 *
 * @example
 * // app/(admin)/layout.tsx — 관리자 레이아웃
 * <AdminContainer
 *   nav={nav}
 *   pathname={pathname}
 *   LinkComponent={Link}
 *   user={user}
 *   onLogout={() => signOut()}
 * >
 *   {children}
 * </AdminContainer>
 */
import { type CSSProperties, type ReactNode, useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import type {
  AdminUser,
  HeaderTheme,
  LinkComponent,
  NavGroup,
  NavItem,
  SidebarTheme,
} from "../../types";
import { useIsMobile } from "../../hooks/use-is-mobile";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { TooltipProvider } from "../ui/tooltip";
import { AdminHeader } from "./admin-header";
import { AdminLayoutProvider } from "./admin-layout-context";
import { AdminSidebar, sidebarThemeToStyle } from "./admin-sidebar";

export interface AdminContainerProps {
  // ── 컨테이너 옵션 ──────────────────────────────────────────────────────────
  /** 최대 너비 (px 또는 CSS 값). 모바일 웹뷰 너비 제한 시 사용. */
  maxWidth?: number | string;
  /** 컨테이너 클래스 */
  containerClassName?: string;

  // ── 메뉴/헤더 (nav 없으면 순수 컨테이너로 동작) ────────────────────────────
  /** 사이드 메뉴 구성. 없으면 메뉴/헤더를 렌더하지 않는다. */
  nav?: NavItem[] | NavGroup[];
  /** 현재 활성 경로 (Next.js: usePathname()) */
  pathname?: string;
  /** 라우팅 링크 컴포넌트 */
  LinkComponent?: LinkComponent;
  /** 사이드바 로고/브랜드 영역 */
  logo?: ReactNode | ((collapsed: boolean) => ReactNode);
  /** 사이드바 하단 고정 영역 */
  sidebarFooter?: ReactNode;
  /** 사이드바 색상 테마 */
  sidebarTheme?: SidebarTheme;

  /** 헤더 타이틀 */
  title?: ReactNode;
  /** 로그인 사용자 정보 */
  user?: AdminUser;
  /** 헤더 우측 추가 액션 */
  headerActions?: ReactNode;
  /** 로그아웃 콜백 */
  onLogout?: () => void;
  /** 사용자 드롭다운 커스텀 항목 */
  userMenuItems?: ReactNode;
  /** 기본 "프로필" 메뉴 클릭 시 이동할 경로 */
  profileHref?: string;
  /** 헤더 색상 테마 */
  headerTheme?: HeaderTheme;
  /** 기본 헤더 대신 완전히 커스텀한 헤더 */
  header?: ReactNode;

  /** 초기 사이드바 접힘 상태 */
  defaultCollapsed?: boolean;
  /** 본문 영역 클래스 */
  contentClassName?: string;

  children: ReactNode;
}

export function AdminContainer({
  maxWidth,
  containerClassName,
  nav,
  pathname,
  LinkComponent,
  logo,
  sidebarFooter,
  sidebarTheme,
  title,
  user,
  headerActions,
  onLogout,
  userMenuItems,
  profileHref,
  headerTheme,
  header,
  defaultCollapsed = false,
  contentClassName,
  children,
}: AdminContainerProps) {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  const ctxValue = useMemo(
    () => ({
      collapsed,
      setCollapsed,
      toggleCollapsed: () => setCollapsed((v) => !v),
      mobileOpen,
      setMobileOpen,
      isMobile,
    }),
    [collapsed, mobileOpen, isMobile]
  );

  const containerStyle: CSSProperties =
    maxWidth != null
      ? typeof maxWidth === "number"
        ? { maxWidth: `${maxWidth}px` }
        : { maxWidth }
      : {};

  // nav 없으면 순수 컨테이너 — 로그인 페이지 등
  if (!nav) {
    return (
      <div
        style={containerStyle}
        className={cn(
          maxWidth != null && "mx-auto",
          "min-h-screen w-full",
          containerClassName
        )}
      >
        {children}
      </div>
    );
  }

  // nav 있으면 풀 관리자 레이아웃
  return (
    <div
      style={containerStyle}
      className={cn(maxWidth != null && "mx-auto", "w-full", containerClassName)}
    >
      <TooltipProvider>
        <AdminLayoutProvider value={ctxValue}>
          <div className="flex min-h-screen w-full bg-background">
            {/* 데스크탑 사이드바 */}
            {!isMobile && (
              <aside
                data-collapsed={collapsed}
                style={sidebarThemeToStyle(sidebarTheme)}
                className={cn(
                  "sticky top-0 h-screen shrink-0 border-r border-sidebar-border transition-[width] duration-200",
                  collapsed ? "w-16" : "w-64"
                )}
              >
                <AdminSidebar
                  nav={nav}
                  pathname={pathname}
                  LinkComponent={LinkComponent}
                  logo={logo}
                  footer={collapsed ? undefined : sidebarFooter}
                  collapsed={collapsed}
                  theme={sidebarTheme}
                />
              </aside>
            )}

            {/* 모바일 Drawer */}
            <Sheet open={isMobile && mobileOpen} onOpenChange={setMobileOpen}>
              <SheetContent side="left" className="w-72 p-0">
                <SheetTitle className="sr-only">사이드 메뉴</SheetTitle>
                <AdminSidebar
                  nav={nav}
                  pathname={pathname}
                  LinkComponent={LinkComponent}
                  logo={logo}
                  footer={sidebarFooter}
                  theme={sidebarTheme}
                  onNavigate={() => setMobileOpen(false)}
                />
              </SheetContent>
            </Sheet>

            {/* 본문 컬럼 */}
            <div className="flex min-w-0 flex-1 flex-col">
              {header ?? (
                <AdminHeader
                  title={title}
                  user={user}
                  actions={headerActions}
                  onLogout={onLogout}
                  userMenuItems={userMenuItems}
                  profileHref={profileHref}
                  LinkComponent={LinkComponent}
                  theme={headerTheme}
                />
              )}
              <main
                className={cn(
                  "flex-1 bg-muted/40 px-4 py-4 sm:px-8 sm:py-6",
                  contentClassName
                )}
              >
                {children}
              </main>
            </div>
          </div>
        </AdminLayoutProvider>
      </TooltipProvider>
    </div>
  );
}
